import { Component, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from "@angular/material/dialog";
import { HttpClient } from "@angular/common/http";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { SolverService } from "@app/services/rest/solver.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatAnchor, MatButton } from "@angular/material/button";
import { FileDownloadDirective } from "../../shared/directives/file-download.directive";
import { DialogHeaderComponent } from "../../shared/dialog-header/dialog-header.component";
import { MatList, MatListItem } from "@angular/material/list";
import { MatIcon } from "@angular/material/icon";

@Component({
  template: `
    <app-dialog-header title="Solver Actions" (closeClicked)="onClose()">
    </app-dialog-header>

    <mat-dialog-content class="action-buttons-container">
      <p>
        If you notice an issue with the solver output, you can download a dump
        of the solver data for further analysis.
      </p>
      <p>
        If possible, please capture images and times that the problem occurs,
        and include them in a support request.
      </p>

      <div class="action-buttons">
        <a
          mat-flat-button
          color="primary"
          appFileDownload
          [fileDownloadUrl]="dumpPath"
          target="_blank"
          class="action-button"
        >
          <mat-icon>file_download</mat-icon>
          Download Solver Data Dump
        </a>
        @if (canPerformActions) {
          <button
            mat-flat-button
            color="warn"
            (click)="reloadPostcode()"
            class="action-button"
          >
            <mat-icon>location_on</mat-icon>
            Reload Postcodes
          </button>

          <button
            mat-flat-button
            color="warn"
            (click)="reload()"
            class="action-button"
          >
            <mat-icon>refresh</mat-icon>
            Reload Solve State From Database
          </button>
        }
      </div>
    </mat-dialog-content>
  `,
  styles: [
    `
      .action-buttons-container {
        padding: 24px;
      }

      .action-buttons {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .action-button {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 12px;
        width: 100%;
        padding: 16px 24px;
        text-align: left;
        font-size: 14px;

        mat-icon {
          margin: 0;
          margin-left: -12px;
        }
      }
    `
  ],
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatAnchor,
    FileDownloadDirective,
    MatButton,
    DialogHeaderComponent,
    MatIcon
  ]
})
export class SolverScoreDialogComponent {
  private clientConfigService = inject(ClientConfigService);
  dialogRef = inject<MatDialogRef<SolverScoreDialogComponent>>(MatDialogRef);
  private store = inject<Store<any>>(Store);
  data = inject<{
    score: string;
  }>(MAT_DIALOG_DATA);
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  solverService = inject(SolverService);

  public canPerformActions = false;
  public dumpPath: string;

  constructor() {
    const solverService = this.solverService;

    solverService.status().then();

    this.dumpPath = this.clientConfigService.config.serviceUrl + "/solver/dump";
    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.canPerformActions = !!auth && auth.isAdmin;
    });
  }

  reload() {
    this.solverService.restart().then(() => {
      this.snackBar.open(`Reloaded Solver State`, "Dismiss", {
        duration: 10000
      });
    });
  }

  reloadPostcode() {
    this.solverService.reloadPostcode().then(() => {
      this.snackBar.open(`Reloaded Postcode Data`, "Dismiss", {
        duration: 10000
      });
    });
  }

  onClose() {
    this.dialogRef.close();
  }
}
